import React from "react";
import ServiceHeroSection from "../common/service-hero-section";
import { AiBlog } from "@/components/main";
import {
  FeatureGrid,
  OutcomesList,
  ProcessSteps,
  RelatedServices,
  ScenarioGrid,
  ServiceAudience,
  ServiceBottomCta,
  ServiceFaq,
  ServiceSection,
  ServiceSectionHeader,
  SplitContent,
} from "../common/service-content-blocks";

const CAPABILITIES = [
  {
    title: "Multi-step task execution",
    description:
      "Agents plan and complete work across tools, with clear stop conditions and operator visibility.",
  },
  {
    title: "Tool and API use",
    description:
      "Connect to CRMs, ERPs, ticketing, and internal APIs so the agent can read and write where work lives.",
  },
  {
    title: "Context and memory",
    description:
      "Ground actions in policies, history, and live system state instead of one-off prompts.",
  },
  {
    title: "Human-in-the-loop",
    description:
      "Route exceptions, high-risk actions, and edge cases to the right person with an audit trail.",
  },
  {
    title: "Evaluation and monitoring",
    description:
      "Track accuracy, latency, failure modes, and operator feedback after go-live.",
  },
  {
    title: "Security and access control",
    description:
      "Respect roles, secrets, and data boundaries already defined in your environment.",
  },
];

const SCENARIOS = [
  {
    title: "Ops triage and ticket handling",
    description:
      "Read incoming requests, gather context from systems, draft or apply updates, and escalate when confidence is low.",
    outcome: "Faster response with fewer manual lookups.",
  },
  {
    title: "Order and shipment follow-up",
    description:
      "Check status across platforms, notify stakeholders, and open exceptions when SLAs are at risk.",
    outcome: "Fewer status meetings, clearer exception queues.",
  },
  {
    title: "Policy-aware internal actions",
    description:
      "Answer and act within approved policies for HR, finance, or compliance workflows.",
    outcome: "Consistent handling without removing ownership.",
  },
  {
    title: "Data gathering across tools",
    description:
      "Collect fields from email, portals, and APIs, then write structured results into the system of record.",
    outcome: "Less swivel-chair work for analysts and coordinators.",
  },
];

const DELIVERY = [
  {
    title: "Map the workflow",
    description:
      "Identify owners, systems, inputs, and where judgment is required.",
  },
  {
    title: "Design agent behavior",
    description:
      "Define tools, policies, escalation rules, and success criteria.",
  },
  {
    title: "Build and evaluate",
    description:
      "Implement against real data and measure quality with operators.",
  },
  {
    title: "Ship and hand off",
    description:
      "Deploy with monitoring, runbooks, and a path for ongoing improvement.",
  },
];

const ARCHITECTURE = [
  {
    title: "Perception and context",
    description:
      "Ingest messages, documents, and system state so the agent knows what is happening now.",
  },
  {
    title: "Planning and tools",
    description:
      "Choose steps, call APIs, and keep actions bounded by policy and permissions.",
  },
  {
    title: "Memory and knowledge",
    description:
      "Use short-term task state and approved knowledge so behavior stays consistent.",
  },
  {
    title: "Oversight and audit",
    description:
      "Log decisions, surface exceptions, and keep humans able to intervene.",
  },
];

export const FAQS = [
  {
    question: "How is an AI agent different from a chatbot?",
    answer:
      "A chatbot mainly answers. An agent plans steps, uses tools, updates systems, and completes work with clear guardrails and human review when needed.",
  },
  {
    question: "Do you build on our existing stack?",
    answer:
      "Yes. We design agents to work with your APIs, identity model, and data sources so you do not have to replace operational platforms.",
  },
  {
    question: "How long does a typical engagement take?",
    answer:
      "Focused pilots often land in weeks. Production systems depend on integrations, data readiness, and review requirements. We scope that clearly up front.",
  },
  {
    question: "Who owns the system after launch?",
    answer:
      "You do. We provide documentation, monitoring hooks, and optional support so your team can operate and extend the agent.",
  },
];

function AiAgentDevelopmentComponent({ posts = [] }) {
  return (
    <>
      <ServiceHeroSection
        headlinePrimary="AI agents for"
        headlineHighWhite="real business operations"
        descriptionPrimary="We design and ship agents that understand context, call your systems, and complete multi-step work with human oversight where it matters."
        descriptionSecondary="Built for operations teams that need reliability, auditability, and a clear path from pilot to production."
      />

      <ServiceSection>
        <ServiceSectionHeader
          title="Agents that do work, not just chat"
          lead="We treat agents as operational software: scoped tools, measurable outcomes, and clear ownership after launch."
        />
        <div className="mx-auto mt-10 max-w-3xl space-y-4 text-base leading-relaxed text-ink-muted">
          <p>
            An AI agent plans steps, uses your systems, and completes tasks with
            guardrails. It is not a chatbot that only answers questions. It is
            software that moves work forward inside the tools your teams already
            run.
          </p>
          <p>
            We start from the workflow: who owns the job, which systems hold
            truth, where judgment is required, and how success will be measured.
            That keeps the build grounded and reduces the risk of a demo that
            never reaches production.
          </p>
        </div>
      </ServiceSection>

      <ServiceAudience
        forItems={[
          "You have a repeated operational workflow with clear owners",
          "Systems expose APIs or can be integrated safely",
          "You want human review on exceptions, not every step",
          "You are ready to measure quality against real samples",
        ]}
        notForItems={[
          "You only need a marketing chatbot with scripted replies",
          "There is no identifiable workflow or success metric",
          "Data access and ownership are undefined",
          "You want an open-ended research project without a ship path",
        ]}
      />

      <ServiceSection>
        <ServiceSectionHeader
          title="What our agents can do"
          lead="Practical building blocks for agents that operate inside your workflows, not beside them."
        />
        <FeatureGrid items={CAPABILITIES} />
      </ServiceSection>

      <ServiceSection muted>
        <ServiceSectionHeader
          title="How we structure agent systems"
          lead="A clear architecture keeps agents reliable as tools, policies, and traffic grow."
        />
        <div className="mt-12">
          <SplitContent
            title="Layers that stay operable"
            lead="Each layer has an owner, a failure mode, and a way to observe it in production."
            points={ARCHITECTURE}
          />
        </div>
      </ServiceSection>

      <ServiceSection>
        <ServiceSectionHeader
          title="Where agents pay off first"
          lead="Common starting points with outcomes operators can recognize."
        />
        <ScenarioGrid items={SCENARIOS} />
      </ServiceSection>

      <ServiceSection muted>
        <ServiceSectionHeader
          title="How we deliver"
          lead="From workflow map to a system your team can run."
        />
        <ProcessSteps steps={DELIVERY} />
      </ServiceSection>

      <OutcomesList
        title="What good looks like"
        lead="We align on outcomes before build starts."
        outcomes={[
          "Measurable reduction in cycle time or manual touches",
          "Exception queues that operators trust",
          "Audit trails for actions taken by the agent",
          "Documentation and monitoring for day-two ownership",
        ]}
      />

      <ServiceFaq items={FAQS} />
      <ServiceBottomCta
        title="Ready to scope an agent?"
        description="Share the workflow, systems, and constraints. We will tell you what is realistic to pilot first."
      />
      <RelatedServices
        items={[
          { href: "/custom-llm-application-development", label: "LLM applications" },
          { href: "/ai-workflow-automation", label: "Workflow automation" },
          { href: "/ai-integrations", label: "Integrations" },
          { href: "/mvp-poc-development", label: "MVP and PoC" },
        ]}
      />
      <AiBlog posts={posts} />
    </>
  );
}

export default AiAgentDevelopmentComponent;
